import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { blogPosts as mockBlogPosts } from '../mock';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { blogAPI, newsletterAPI } from '../utils/api';
import { useToast } from '../hooks/use-toast';

export const Blog = () => {
  const { toast } = useToast();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await blogAPI.getAll();
        if (response.data && response.data.length > 0) {
          setBlogPosts(response.data);
        } else {
          // Fallback to mock data if no posts in DB
          setBlogPosts(mockBlogPosts);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        // Fallback to mock data on error
        setBlogPosts(mockBlogPosts);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      await newsletterAPI.subscribe(email);
      toast({
        title: "Subscribed!",
        description: "Thank you for subscribing to our newsletter.",
      });
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast({
        title: "Already Subscribed",
        description: "This email is already subscribed to our newsletter.",
      });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section id="blog" className="py-20 bg-gradient-to-br from-slate-50 to-emerald-50" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">
            Resources & Insights
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Data-driven articles about living, working, and thriving in Cornwall
          </p>
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-slate-500">Loading articles...</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {blogPosts.slice(0, 4).map((post) => (
              <Card
                key={post.id}
                className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 hover:border-emerald-300 flex flex-col"
                data-testid={`blog-card-${post.id}`}
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden bg-slate-200">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800';
                    }}
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      {post.category}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="space-y-3">
                  <CardTitle className="text-2xl font-bold text-slate-900 group-hover:text-emerald-600 transition-colors">
                    {post.title}
                  </CardTitle>
                  <div className="flex items-center space-x-4 text-sm text-slate-500">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(post.created_at || post.date).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.read_time || post.readTime}</span>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-grow">
                  <CardDescription className="text-slate-600 text-base leading-relaxed">
                    {post.excerpt}
                  </CardDescription>
                </CardContent>

                <CardFooter>
                  <Link to={`/blog/${post.id}`} className="w-full">
                    <Button
                      variant="ghost"
                      className="group/btn text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 w-full justify-between"
                    >
                      Read Full Article
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Newsletter CTA */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg border-2 border-emerald-200">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h3 className="text-3xl font-bold text-slate-900">
              Stay Updated on Cornwall
            </h3>
            <p className="text-slate-600 text-lg">
              Get the latest insights, market data, and community news delivered to your inbox monthly
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500 transition-colors"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit"
                disabled={subscribing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 shadow-md hover:shadow-lg transition-all"
                data-testid="newsletter-subscribe-btn"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
            <p className="text-xs text-slate-500">
              No spam, unsubscribe anytime. We respect your privacy.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
